import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface AdminPlaceDispute {
  id: string;
  place_id: string;
  place_name: string | null;
  place_category: string | null;
  place_categories: string[] | null;
  place_address: string | null;
  place_latitude: number | null;
  place_longitude: number | null;
  reporter_id: string | null;
  reporter_name: string | null;
  reason: string;
  contact: any;
  status: string;
  created_at: string;
  resolved_at: string | null;
}

export type DisputeAction = 'reject' | 'delete_place' | 'remove_owner' | 'mark_resolved';

export function useAdminPlaceDisputes(status: string | null = 'pending') {
  return useQuery({
    queryKey: ['admin-place-disputes', status],
    queryFn: async (): Promise<AdminPlaceDispute[]> => {
      const { data, error } = await (supabase as any).rpc('admin_list_place_disputes', { p_status: status });
      if (error) throw error;
      return (data as AdminPlaceDispute[]) ?? [];
    },
  });
}

export function useResolvePlaceDispute() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { disputeId: string; action: DisputeAction; notes?: string }) => {
      const { data, error } = await (supabase as any).rpc('admin_resolve_place_dispute', {
        p_dispute_id: v.disputeId,
        p_action: v.action,
        p_notes: v.notes ?? null,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-place-disputes'] });
      qc.invalidateQueries({ queryKey: ['pet-places'] });
    },
  });
}

export function useAdminUpdatePlace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: {
      placeId: string;
      name?: string;
      category?: string;
      categories?: string[];
      address?: string;
      pet_policy_summary?: string;
      is_verified?: boolean;
    }) => {
      const { data, error } = await (supabase as any).rpc('admin_update_pet_place', {
        p_place_id: v.placeId,
        p_name: v.name ?? null,
        p_category: v.category ?? null,
        p_address: v.address ?? null,
        p_pet_policy_summary: v.pet_policy_summary ?? null,
        p_is_verified: v.is_verified ?? null,
        p_categories: v.categories ?? null,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-place-disputes'] });
      qc.invalidateQueries({ queryKey: ['pet-places'] });
    },
  });
}
